import { Component } from '@theme/component';

const FILTER_FORM_SELECTOR = '[data-atc-media-filter-form]';
const FILTER_SELECTOR = '[data-atc-media-filter]';
const FILTER_PILL_SELECTOR = '[data-atc-music-releases-filter-pill]';
const CLEAR_FILTERS_SELECTOR = '[data-atc-media-clear-filters]';
const LOAD_MORE_SELECTOR = '[data-atc-music-releases-load-more]';
const RESULTS_SELECTOR = '[data-atc-music-releases-results]';
const ITEM_TEMPLATE_SELECTOR = '[data-atc-music-releases-item]';
const EMPTY_SELECTOR = '.atc-music-releases__empty';
const STATUS_SELECTOR = '[data-atc-music-releases-status]';
const COUNT_BADGE_SELECTOR = '[data-atc-music-releases-count-badge]';
const FACET_BUBBLE_SELECTOR = '.facets__status-wrapper .hide-when-empty';
const PARAM_RELEASE_TYPE = 'music_release_type';
const PARAM_ERA = 'music_release_era';
const PARAM_COUNT = 'music_release_count';

/**
 * ATC: overlay: Replaces fetch/section-morph filtering with pure client-side DOM
 * management. All release items are pre-rendered in inert <template> elements by
 * Liquid; JavaScript reads URL params, clones matching templates into the grid,
 * and updates filter UI without any network requests.
 *
 * @typedef {object} Refs
 * @extends {Component<Refs>}
 */
class AtcMusicReleases extends Component {
  /** @type {Set<string>} */
  #reportedIssues = new Set();

  /** @type {number | null} */
  #pendingRaf = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('popstate', this.#handlePopState);
    this.#syncFromUrl();
    this.#reportInvalidFilters();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('popstate', this.#handlePopState);

    if (this.#pendingRaf !== null) {
      cancelAnimationFrame(this.#pendingRaf);
      this.#pendingRaf = null;
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reportInvalidFilters();
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get itemsPerLoad() {
    return Number(this.dataset.itemsPerLoad) || 1;
  }

  get maxItems() {
    return Number(this.dataset.maxItems) || 0;
  }

  get limitItems() {
    return this.dataset.limitItems === 'true';
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.matches(FILTER_SELECTOR)) return;

    const form = event.target.closest(FILTER_FORM_SELECTOR);
    if (!(form instanceof HTMLFormElement)) return;

    const url = this.#buildFilterUrl(form);
    this.#navigate(url);
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FILTER_FORM_SELECTOR)) return;

    event.preventDefault();
    const url = this.#buildFilterUrl(form);
    this.#navigate(url);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const clearFilters = event.target.closest(CLEAR_FILTERS_SELECTOR);

    if (clearFilters instanceof HTMLElement && this.contains(clearFilters)) {
      event.preventDefault();
      const url = this.#buildClearFiltersUrl();
      this.#navigate(url);
      return;
    }

    const pill = event.target.closest(FILTER_PILL_SELECTOR);

    if (pill instanceof HTMLElement && this.contains(pill)) {
      event.preventDefault();
      const param = pill.dataset.filterParam || '';
      const value = pill.dataset.filterValue || '';
      const url = new URL(window.location.href);

      if (value) {
        url.searchParams.set(param, value);
      } else {
        url.searchParams.delete(param);
      }

      this.#setCountParam(url, this.itemsPerLoad);
      this.#navigate(url);
      return;
    }

    const loadMore = event.target.closest(LOAD_MORE_SELECTOR);
    if (!(loadMore instanceof HTMLElement) || !this.contains(loadMore)) return;

    event.preventDefault();
    const url = new URL(window.location.href);
    const nextCount = Number(loadMore.dataset.nextCount);

    if (nextCount > 0) {
      this.#setCountParam(url, nextCount);
    }

    this.#navigate(url, { replace: true });
  };

  #handlePopState = () => {
    this.#syncFromUrl();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {URL}
   */
  #buildFilterUrl(form) {
    const formData = new FormData(form);
    const url = new URL(window.location.href);
    const releaseTypeParam = this.#getParamName(PARAM_RELEASE_TYPE);
    const eraParam = this.#getParamName(PARAM_ERA);

    url.searchParams.delete(releaseTypeParam);
    url.searchParams.delete(eraParam);
    this.#setCountParam(url, this.itemsPerLoad);

    const selectedReleaseTypes = formData.getAll(releaseTypeParam).map(String).filter(Boolean);
    const selectedEras = formData.getAll(eraParam).map(String).filter(Boolean);

    if (selectedReleaseTypes.length > 0) {
      url.searchParams.set(releaseTypeParam, selectedReleaseTypes.join(','));
    }

    if (selectedEras.length > 0) {
      url.searchParams.set(eraParam, selectedEras.join(','));
    }

    return url;
  }

  /** @returns {URL} */
  #buildClearFiltersUrl() {
    const url = new URL(window.location.href);

    url.searchParams.delete(this.#getParamName(PARAM_RELEASE_TYPE));
    url.searchParams.delete(this.#getParamName(PARAM_ERA));
    this.#setCountParam(url, this.itemsPerLoad);

    return url;
  }

  /**
   * Sets the count URL param, omitting it when the value equals the default
   * items-per-load so that default-state URLs stay clean.
   *
   * @param {URL} url
   * @param {number} count
   */
  #setCountParam(url, count) {
    const countParam = this.#getParamName(PARAM_COUNT);

    if (count === this.itemsPerLoad) {
      url.searchParams.delete(countParam);
    } else {
      url.searchParams.set(countParam, String(count));
    }
  }

  /**
   * @param {URL} url
   * @param {{ replace?: boolean }} [options]
   */
  #navigate(url, { replace = false } = {}) {
    if (replace) {
      history.replaceState({ musicReleases: true }, '', url.toString());
    } else {
      history.pushState({ musicReleases: true }, '', url.toString());
    }

    this.#scheduleSyncFromUrl();
  }

  #scheduleSyncFromUrl() {
    if (this.#pendingRaf !== null) {
      cancelAnimationFrame(this.#pendingRaf);
    }
    this.#pendingRaf = requestAnimationFrame(() => {
      this.#pendingRaf = null;
      this.#syncFromUrl();
    });
  }

  #syncFromUrl() {
    const url = new URL(window.location.href);
    const releaseTypeValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_RELEASE_TYPE))
    );
    const eraValues = this.#parseFilterParam(
      url.searchParams.get(this.#getParamName(PARAM_ERA))
    );
    const requestedCount =
      Number(url.searchParams.get(this.#getParamName(PARAM_COUNT))) || this.itemsPerLoad;

    this.#syncCheckboxes(this.#getParamName(PARAM_RELEASE_TYPE), releaseTypeValues);
    this.#syncCheckboxes(this.#getParamName(PARAM_ERA), eraValues);
    this.#updateGrid(releaseTypeValues, eraValues, requestedCount);
    this.#updateFacetBubbles();
    this.#updateFilterUI(releaseTypeValues.length + eraValues.length);
    this.#syncPills();
  }

  /**
   * @param {string | null} value
   * @returns {string[]}
   */
  #parseFilterParam(value) {
    if (!value) return [];

    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  /**
   * @param {string} paramName
   * @param {string[]} activeValues
   */
  #syncCheckboxes(paramName, activeValues) {
    const activeSet = new Set(activeValues);

    for (const input of this.querySelectorAll(FILTER_SELECTOR)) {
      if (!(input instanceof HTMLInputElement) || input.name !== paramName) continue;

      input.checked = activeSet.has(input.value);
      const item = input.closest('.facets__inputs-list-item');

      if (item instanceof HTMLElement) {
        item.classList.toggle('facets__inputs-list-item--active', input.checked);
      }
    }
  }

  /**
   * @param {string[]} releaseTypeValues
   * @param {string[]} eraValues
   * @param {number} requestedCount
   */
  #updateGrid(releaseTypeValues, eraValues, requestedCount) {
    const results = this.querySelector(RESULTS_SELECTOR);
    const emptyEl = this.querySelector(EMPTY_SELECTOR);
    if (!(results instanceof HTMLElement)) return;

    const releaseTypeSet = new Set(releaseTypeValues);
    const eraSet = new Set(eraValues);
    const hasReleaseTypeFilter = releaseTypeSet.size > 0;
    const hasEraFilter = eraSet.size > 0;

    /** @type {HTMLTemplateElement[]} */
    const matched = [];

    for (const template of this.querySelectorAll(ITEM_TEMPLATE_SELECTOR)) {
      if (!(template instanceof HTMLTemplateElement)) continue;

      const releaseType = template.dataset.releaseType || '';
      const eraId = template.dataset.eraId || '';

      if (hasReleaseTypeFilter && !releaseTypeSet.has(releaseType)) continue;
      if (hasEraFilter && !eraSet.has(eraId)) continue;

      matched.push(template);
    }

    let visibleCount = requestedCount;

    if (this.limitItems && this.maxItems > 0 && visibleCount > this.maxItems) {
      visibleCount = this.maxItems;
    }

    if (visibleCount > matched.length) {
      visibleCount = matched.length;
    }

    results.innerHTML = '';

    for (let i = 0; i < visibleCount; i++) {
      results.appendChild(matched[i].content.cloneNode(true));
    }

    if (emptyEl instanceof HTMLElement) {
      if (visibleCount === 0) {
        const hasFilters = hasReleaseTypeFilter || hasEraFilter;
        emptyEl.textContent = hasFilters
          ? 'No releases for the selected filters.'
          : 'No music releases are available.';
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    const nextVisibleCount = this.#computeNextVisibleCount(visibleCount, matched.length);
    this.#updateLoadMore(nextVisibleCount > visibleCount, nextVisibleCount);
    this.#updateStatus(matched.length);
  }

  /**
   * @param {number} currentCount
   * @param {number} totalMatched
   * @returns {number}
   */
  #computeNextVisibleCount(currentCount, totalMatched) {
    let next = currentCount + this.itemsPerLoad;

    if (this.limitItems && this.maxItems > 0 && next > this.maxItems) {
      next = this.maxItems;
    }

    if (next > totalMatched) {
      next = totalMatched;
    }

    return next;
  }

  /**
   * @param {boolean} hasMore
   * @param {number} nextCount
   */
  #updateLoadMore(hasMore, nextCount) {
    const trigger = this.querySelector(LOAD_MORE_SELECTOR);
    if (!(trigger instanceof HTMLElement)) return;

    trigger.dataset.nextCount = String(nextCount);

    if (hasMore) {
      trigger.removeAttribute('hidden');
    } else {
      trigger.setAttribute('hidden', '');
    }
  }

  /** @param {number} matchedCount */
  #updateStatus(matchedCount) {
    let text = '';

    if (matchedCount === 0) {
      text = 'No releases for the selected filters.';
    } else if (matchedCount === 1) {
      text = '1 release selected.';
    } else {
      text = `${matchedCount} releases selected.`;
    }

    for (const statusEl of this.querySelectorAll(STATUS_SELECTOR)) {
      statusEl.textContent = text;
    }
  }

  #updateFacetBubbles() {
    for (const details of this.querySelectorAll('details')) {
      if (!(details instanceof HTMLElement)) continue;

      const checkedCount = details.querySelectorAll(`${FILTER_SELECTOR}:checked`).length;
      const bubble = details.querySelector(FACET_BUBBLE_SELECTOR);
      if (!(bubble instanceof HTMLElement)) continue;

      bubble.textContent = checkedCount > 0 ? String(checkedCount) : '';

      if (checkedCount > 0) {
        bubble.classList.add('bubble', 'facets__bubble');
      } else {
        bubble.classList.remove('bubble', 'facets__bubble');
      }
    }
  }

  /** @param {number} activeCount */
  #updateFilterUI(activeCount) {
    for (const btn of this.querySelectorAll(CLEAR_FILTERS_SELECTOR)) {
      if (!(btn instanceof HTMLElement)) continue;

      if (activeCount > 0) {
        btn.removeAttribute('hidden');
      } else {
        btn.setAttribute('hidden', '');
      }
    }

    for (const badge of this.querySelectorAll(COUNT_BADGE_SELECTOR)) {
      if (!(badge instanceof HTMLElement)) continue;

      if (activeCount > 0) {
        badge.removeAttribute('hidden');

        if (badge.classList.contains('filter-count-bubble')) {
          badge.setAttribute('aria-label', `${activeCount} active filters`);
          const text = badge.querySelector('.filter-count-bubble__text');
          if (text instanceof HTMLElement) text.textContent = String(activeCount);
        } else {
          badge.textContent = String(activeCount);
        }
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  }

  #syncPills() {
    const url = new URL(window.location.href);

    for (const pill of this.querySelectorAll(FILTER_PILL_SELECTOR)) {
      if (!(pill instanceof HTMLElement)) continue;

      const param = pill.dataset.filterParam || '';
      const value = pill.dataset.filterValue || '';
      const currentValue = url.searchParams.get(param) || '';
      const isActive = currentValue === value;

      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      pill.classList.toggle('atc-music-releases__filter-pill--active', isActive);
    }
  }

  #reportInvalidFilters() {
    const filterConfigurations = [
      [this.#getParamName(PARAM_RELEASE_TYPE), this.dataset.validReleaseTypeFilterIds],
      [this.#getParamName(PARAM_ERA), this.dataset.validEraFilterIds],
    ];
    const url = new URL(window.location.href);

    for (const [param, validIds] of filterConfigurations) {
      const allowedIds = new Set((validIds || '').split('|').filter(Boolean));
      const requestedIds = url.searchParams
        .getAll(param)
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean);

      for (const requestedId of requestedIds) {
        if (allowedIds.has(requestedId)) continue;
        this.#warnOnce(`invalid_filter_value:${param}:${requestedId}`, param, requestedId);
      }
    }
  }

  /**
   * @param {string} key
   * @param {string} kind
   * @param {string} reference
   */
  #warnOnce(key, kind, reference) {
    if (this.#reportedIssues.has(key)) return;

    this.#reportedIssues.add(key);
    console.warn('[atc-music-releases] Invalid release filter was ignored.', {
      sectionId: this.sectionId,
      kind,
      reference,
    });
  }

  /**
   * @param {string} param
   * @returns {string}
   */
  #getParamName(param) {
    return `${param}_${this.sectionId}`;
  }
}

if (!customElements.get('atc-music-releases')) {
  customElements.define('atc-music-releases', AtcMusicReleases);
}
